import React from "react";
import { useI18n } from "@/context/I18nContext";
import { stat } from "@/lib/i18nFormat";

export const PageHeader: React.FC<{ title: string; subtitle?: string; actions?: React.ReactNode; testId?: string }> = ({
  title, subtitle, actions, testId,
}) => {
  const { t } = useI18n();
  return (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-3" data-testid={testId}>
    <div>
      <h1 className="font-display text-2xl font-bold lg:text-3xl">{t(title)}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{t(subtitle)}</p>}
    </div>
    {actions}
  </div>
  );
};

const STATUS_MAP: Record<string, { cls: string; label?: string }> = {
  active: { cls: "bg-status-good/12 text-status-good ring-status-good/25" },
  inactive: { cls: "bg-muted text-muted-foreground ring-border" },
  good: { cls: "bg-status-good/12 text-status-good ring-status-good/25" },
  warn: { cls: "bg-status-warn/12 text-status-warn ring-status-warn/25" },
  critical: { cls: "bg-status-critical/12 text-status-critical ring-status-critical/25" },
  info: { cls: "bg-status-info/12 text-status-info ring-status-info/25" },
};

export const StatusPill: React.FC<{ status: string; children?: React.ReactNode; testId?: string }> = ({ status, children, testId }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.info;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${s.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children || stat(status)}
    </span>
  );
};

export const LocalTime: React.FC<{ tz: string }> = ({ tz }) => {
  const { locale } = useI18n();
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const bcp = locale === "es" ? "es-US" : "en-US";
  let time = "—";
  let date = "";
  try {
    time = new Intl.DateTimeFormat(bcp, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(now);
    date = new Intl.DateTimeFormat(bcp, { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(now);
  } catch { /* invalid tz */ }
  return <span title={tz}>{time} · {date}</span>;
};

export const EmptyState: React.FC<{ title: string; description?: string; icon?: React.ReactNode }> = ({ title, description, icon }) => {
  const { t } = useI18n();
  return (
  <div className="rounded-lg border border-dashed border-border bg-card/50 p-10 text-center" data-testid="empty-state">
    {icon && <div className="mx-auto mb-3 text-muted-foreground">{icon}</div>}
    <div className="font-display text-base font-semibold">{t(title)}</div>
    {description && <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t(description)}</p>}
  </div>
  );
};

export const LoadingBlock: React.FC = () => (
  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground" data-testid="loading-block">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
  </div>
);
